/**
 * Self-contained diff helpers for the Knowledge Base article version history.
 *
 * Article content is stored as HTML (the RichTextEditor emits HTML). To show a
 * readable "what changed" view we first flatten the HTML to plain-text lines,
 * then run a classic LCS line diff. No external diff dependency is used (none is
 * installed in the frontend), so these stay pure and unit-testable.
 */

export type DiffLineType = 'added' | 'removed' | 'unchanged';

export interface DiffLine {
  type: DiffLineType;
  text: string;
}

/**
 * Flatten article HTML into readable plain-text lines for diffing.
 * Block boundaries (</p>, </li>, <br>, headings, …) become newlines, remaining
 * tags are stripped, common entities are decoded, and runs of blank lines are
 * collapsed.
 */
export function htmlToText(html: string): string {
  if (!html) return '';
  return html
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\s*(li|tr|p|div|h[1-6]|blockquote|pre)[^>]*>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6]|tr|blockquote|pre|ul|ol|table)\s*>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .split('\n')
    .map((line) => line.trim())
    // drop blank lines — they only add noise to the line diff
    .filter((line) => line !== '')
    .join('\n')
    .trim();
}

/**
 * Line-level diff of two texts via longest-common-subsequence backtracking.
 * `added` = present in `newText` only, `removed` = present in `oldText` only.
 */
export function diffLines(oldText: string, newText: string): DiffLine[] {
  const a = oldText ? oldText.split('\n') : [];
  const b = newText ? newText.split('\n') : [];
  const n = a.length;
  const m = b.length;

  // dp[i][j] = LCS length of a[i:] and b[j:]
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ type: 'unchanged', text: a[i] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ type: 'removed', text: a[i] });
      i++;
    } else {
      out.push({ type: 'added', text: b[j] });
      j++;
    }
  }
  while (i < n) out.push({ type: 'removed', text: a[i++] });
  while (j < m) out.push({ type: 'added', text: b[j++] });
  return out;
}